/**
 * Repairs inline-image URLs stored by earlier versions of the upload endpoint.
 *
 * Descriptions persist whatever URL the backend returned at paste time, and those URLs
 * are also mirrored into GitHub issue bodies — so they can't be migrated after the fact.
 * They're rewritten at render time instead. Two historical shapes need fixing:
 *
 *  1. Absolute, missing the `/api` prefix:
 *       https://<backend-host>/product-feedback/inline-image/...
 *     The browser reaches the backend at `/api/*` on the frontend's own origin, so this
 *     landed on the SPA catch-all and returned index.html — a 200 of text/html, which
 *     renders as a broken image.
 *
 *  2. The path segment percent-encoded (`feedback-attachments%2Ffile.png`). Proxies and
 *     Express decode %2F back to "/" before routing, which stopped the single-segment
 *     route matching and fell through to auth, producing a 401.
 *
 * Both normalise to `/api/product-feedback/inline-image/feedback-attachments/<file>`.
 */
const INLINE_IMAGE_PATH = '/product-feedback/inline-image/';

export const normaliseInlineImageSrc = (src?: string): string | undefined => {
  if (!src) return src;
  const idx = src.indexOf(INLINE_IMAGE_PATH);
  if (idx === -1) return src;

  // Everything after the route prefix is the repo path, however it was encoded.
  const rawPath = src.slice(idx + INLINE_IMAGE_PATH.length);
  let decoded: string;
  try {
    decoded = decodeURIComponent(rawPath);
  } catch {
    // Malformed escape — leave it alone rather than throwing during render.
    return src;
  }

  const encoded = decoded.split('/').map(encodeURIComponent).join('/');
  return `/api${INLINE_IMAGE_PATH}${encoded}`;
};
